import React, { PureComponent, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Select } from "antd";
// import { Utils as QbUtils } from "@react-awesome-query-builder/ui";
import omit from "lodash/omit";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const SELECT_WIDTH_OFFSET_RIGHT = 48;
const calcTextWidth = (str) => {
  if (!str) return 0;
  if (typeof document !== "undefined") {
    var div = document.createElement("div");
    div.innerText = str;
    var css = {
      "position": "absolute",
      "float": "left",
      "white-space": "nowrap",
      "visibility": "hidden",
      "font-size": "14px",
      "font-family": "'Helvetica Neue', Helvetica, Arial, sans-serif"
    };
    for (var k in css) {
      div.style[k] = css[k];
    }
    div = document.body.appendChild(div);
    var w = div.offsetWidth;
    document.body.removeChild(div);
    return w;
  }
  return 0;
};


const mapListValues = (listValues, mapFn) => {
  if (!listValues) return [];
  return Array.isArray(listValues) ? listValues.map(mapFn) : Object.keys(listValues).map(k => mapFn(listValues[k], k));
};

const Option = Select.Option;

export default class MultiSelectWidget extends PureComponent {
  static propTypes = {
    setValue: PropTypes.func.isRequired,
    config: PropTypes.object.isRequired,
    value: PropTypes.array,
    field: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    customProps: PropTypes.object,
    fieldDefinition: PropTypes.object,
    readonly: PropTypes.bool,
    // from fieldSettings:
    listValues: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    allowCustomValues: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    
    this.state = {
      showModal: false,
      selectedYearRange: (this.props.value || []).filter(Boolean),
    };
  }

  componentDidMount() {
    const cleanValue = (this.props.value || []).filter(Boolean);
    if(cleanValue.length) {
      this.props.setValue(cleanValue);
      this.handleYearsRange(cleanValue);
      this.handleChange(cleanValue);
    }
  }

  handleChange = (val) => {

    if (val && !val.length) {
      val = undefined; //not allow []
    }
    //Split on separators, space or comma, if allow custom values
    let newValues=[];
    let uniqueValues = {};
    const allowCustomValues = this.props.allowCustomValues || this.props.fieldDefinition?.allowCustomValues || this.props.fieldDefinition?.fieldSettings?.allowCustomValues;
    if (Array.isArray(val) && val.length > 0 && allowCustomValues) {
      val.forEach(record => {
        let values = record.split(/[\s,]+/);
        values.forEach(val => {
          // Additional cleanup to strip invisible zero-width spaces/characters Excel sometimes adds
          let cleanVal = val.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
          if (cleanVal.length > 0) {
            if (!(cleanVal in uniqueValues)) {
              uniqueValues[cleanVal] = 1;
              newValues.push(cleanVal);
            }
          }
        });
      });
      this.props.setValue(newValues);
    } else {
      this.props.setValue(val);
    }
  };

  handleYearsRange = (val) => {
    this.setState(() => {
      return { selectedYearRange: val };
    }, () => {
      this.props.setValue(this.state.selectedYearRange);
    });
  };

  filterOption = (input, option) => {
    const dataForFilter = option.children || option.value;
    return dataForFilter.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  render() {
    const {config, placeholder, customProps, value, readonly, field, listValues, fieldDefinition} = this.props;
    const allowCustomValues = this.props.allowCustomValues || fieldDefinition?.allowCustomValues || fieldDefinition?.fieldSettings?.allowCustomValues;
    const {renderSize} = config.settings;
    const placeholderWidth = calcTextWidth(placeholder);
    const aValue = value && value.length ? value : undefined;
    
    let optionsMaxWidth = 0;
    const options = mapListValues(listValues, ({title, value}) => {
      optionsMaxWidth = Math.max(optionsMaxWidth, calcTextWidth(title, null));
      return (<Option key={value} value={value}>{title}</Option>);
    });

    const width = aValue ? null : placeholderWidth + SELECT_WIDTH_OFFSET_RIGHT;
    const dropdownWidth = optionsMaxWidth + SELECT_WIDTH_OFFSET_RIGHT;
    const customSelectProps = omit(customProps, ["showCheckboxes"]);


    
    // Identify the exact Modal to show using the explicit uiType passed down from the backend API.
    // In RAQB v6, fieldSettings is spread flat into widget props, so this.props.uiType is the
    // most reliable source. field prop may be null if the field is an Immutable in v6.
    const uiType = this.props.uiType
      || this.props.fieldDefinition?.fieldSettings?.uiType
      || this.props.config?.fields?.[field]?.fieldSettings?.uiType;

    const isBday = uiType === 'birthdate-range' || field === "ameps__v-epsln-demog-000033";
    const isDate = uiType === 'date-range' || field === "ameps__v-epsln-demog-000043";
    const isStringRange = uiType === 'year-range' || uiType === 'age-range' || uiType === 'int-range' || uiType === 'float-range' || field === "ameps__v-epsln-demog-000031" || field === "ameps__v-epsln-demog-000030";

    // modal helpers
    const toggleModal = () => {
      this.setState({showModal: !this.state.showModal});
    };

    let currentRangeSelections = (this.state.selectedYearRange || []).filter(Boolean).map(item => 
      item
        .split("|")
        .map(value => value.startsWith("0") ? value.slice(1) : value)
        .join("|")
    );

    if(isStringRange) {
      return (
        <>
          {!readonly && <button type="button" className="add-edit-range" onClick={toggleModal}>Add/Edit</button>}
          {
            currentRangeSelections.length ? (<span className="range-wrapper">{currentRangeSelections.map((range) => {
              return (
                <span className="range-details" key={range}>{range}</span>
              );
            })}</span>) : ""
          }
          { this.state.showModal && <YearsSelector
            show={this.state.showModal}
            toggle={toggleModal}
            addNew={this.handleYearsRange}
            currentSelections={currentRangeSelections}
            title={this.props.fieldDefinition?.label || "Values Range"}
          />}
        </>
      );
    }

    if(isBday || isDate) {
      return (
        <>
          {!readonly && <button type="button" className="add-edit-range" onClick={toggleModal}>Add/Edit</button>}
          {
            this.state.selectedYearRange.length ? (<span className="range-wrapper">{this.state.selectedYearRange.map((range) => {
              return (
                <span className="range-details" key={range}>{formatDisplayDateRange(range)}</span>
              );
            })}</span>) : ""
          }
          { this.state.showModal && <BdaySelector
            show={this.state.showModal}
            toggle={toggleModal}
            addNew={this.handleYearsRange}
            currentSelections={this.state.selectedYearRange}
            isBday={isBday}
            title={this.props.fieldDefinition?.label || (isBday ? "Birthday" : "Date Range")}
          />}
        </>
      );
    }
    
    return (
      <Select
        tokenSeparators={[',', ' ', '\t', '\n']}

        disabled={readonly}
        mode={allowCustomValues ? "tags" : "multiple"}
        style={{
          minWidth: width,
          width: "100%",
        }}
        dropdownStyle={{
          width: dropdownWidth,
        }}
        key={"widget-multiselect"}
        dropdownMatchSelectWidth={false}
        placeholder={placeholder}
        size={renderSize}
        value={aValue}
        onChange={this.handleChange}
        filterOption={this.filterOption}
        {...customSelectProps}
      >{options}
      </Select>
    );
  }
}


/**
 * Modal to select range of numbers
 */

export function YearsSelector({toggle, addNew, show, currentSelections, title}) {

  const [selectedRanges, setSelectedRanges] = useState(currentSelections || []);
  const [startYear, setStartYear] = useState();
  const [endYear, setEndYear] = useState();
  const [ready, setReady] = useState(false);

  const startYearRef = useRef(null);
  const endYearRef = useRef(null);

  const handleAddRange = () => {
    if (startYearRef.current.value && endYearRef.current.value) {
      let start_value = startYearRef.current.value;
      let end_value = endYearRef.current.value;
      let newRange = `${start_value}|${end_value}`;
      if(!selectedRanges.includes(newRange)) {
        let updatedState = [...selectedRanges, newRange];
        setSelectedRanges(updatedState);
        addNew(updatedState);
      }
      toggle();
    }
  };

  const handleUpdateStartYear = () => {
    setStartYear(startYearRef.current.value);
  };

  const handleUpdateEndYear = () => {
    setEndYear(endYearRef.current.value);
  };

  const handleDeleteRange = (el) => {
    let indexToRemove = selectedRanges.indexOf(el);
    let updatedArray = selectedRanges.filter((_,index) => index !== indexToRemove );
    setSelectedRanges(updatedArray);
    addNew(updatedArray);
  };

  useEffect(() => {
    const s = Number(startYear);
    const e = Number(endYear);
    if(startYear && endYear && !isNaN(s) && !isNaN(e) && (e >= s)) {
      setReady(true);
    } else {
      setReady(false);
    }
  }, [startYear, endYear]);

  return (<>
    <Modal isOpen={show} className="modal-dialog-centered date-picker">
      <ModalHeader>{title || "Values Range"}</ModalHeader>
      <ModalBody>
        <div className='input-range custom-select'>
          <div className='ir-start'>
            <label>Start Value</label>
            <input 
              type="text" 
              ref={startYearRef} 
              onChange={handleUpdateStartYear} 
              placeholder="Enter a value"
            />
          </div>
          <i className="bi bi-arrow-right"></i>
          <div className='ir-end'>
            <label>End Value</label>
            <input 
              type="text" 
              ref={endYearRef} 
              onChange={handleUpdateEndYear} 
              placeholder="Enter a value"
            />
          </div>
        </div>
        <hr />
        {selectedRanges
            && <div className="year-editor-section">
              {selectedRanges.map((range) => {
                return (<div className="year-range" key={range}>
                  <span className="range">{range}</span>
                  <span className="delete-icon">
                    <i className="bi bi-trash" onClick={() => handleDeleteRange(range)}/>
                  </span>
                </div>);
              })}
            </div>
        }
          
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={toggle}>Close</Button>
        <Button color="primary" className="promote" size="sm" 
          onClick={handleAddRange}
          disabled={!ready}
        >
              Add Selection
        </Button>{" "}
      </ModalFooter>
    </Modal>
  </>);
}


/**
 * Modal to select birthday dates
 */
function subtractYears(date, years) {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() - years);
  return result;
}

// format YYYYMMDD into MM/DD/YYYY-MM/DD/YYYY for display
const formatDisplayDateRange = (date) => {
  if (!date) return;
  let dates = date.split("-");
  let startDate = formatDisplayDate(dates[0]);
  let endDate = formatDisplayDate(dates[1]) || startDate;
  return startDate + " - " + endDate;
};

// format YYYYMMDD into MM/DD/YYYY for display
const formatDisplayDate = (date) => {
  if (!date) return;
  const year = date.substring(0, 4);
  const month = date.substring(4, 6);
  const day = date.substring(6, date.length);

  return month + "/" + day + "/" + year;
};

export function BdaySelector({toggle, addNew, show, currentSelections, isBday, title}) {
  const [selectedRanges, setSelectedRanges] = useState(currentSelections || []);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [minDate] = useState(subtractYears(new Date(), 110));
  const [minEndDate, setMinEndDate] = useState();
  const [ready, setReady] = useState(false);

  const handleStartChange = (val) => {
    // check that end date is not earlier than start date
    if( endDate && (endDate.getTime() < val.getTime()) ) {
      setEndDate(val);
    }
    setStartDate(val);
    setMinEndDate(val);
  };

  const handleEndChange = (val) => {
    setEndDate(val);
  };

  // format date into YYYYMMDD for backend
  const formatDate = (date) => {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) 
      month = "0" + month;
    if (day.length < 2) 
      day = "0" + day;

    return [year, month, day].join("");
  };

  const handleAddRange = () => {
    if (startDate && endDate) {
      let newRange = `${formatDate(startDate)}-${formatDate(endDate)}`;
      if(!selectedRanges.includes(newRange)) {
        let updatedState = [...selectedRanges, newRange];
        setSelectedRanges(updatedState);
        addNew(updatedState);
      }
      toggle();
    }
  };

  const handleDeleteRange = (el) => {
    let indexToRemove = selectedRanges.indexOf(el);
    let updatedArray = selectedRanges.filter((_,index) => index !== indexToRemove );
    setSelectedRanges(updatedArray);
    addNew(updatedArray);
  };

  useEffect(() => {
    if(startDate && endDate) {
      setReady(true);
    } else {
      setReady(false);
    }
  }, [startDate, endDate]);

  return (<>
    <Modal isOpen={show} className="modal-dialog-centered date-picker">
      <ModalHeader>{title || "Date Range"}</ModalHeader>
      <ModalBody>
        <div className='datepicker'>
          <div className='dp-start'>
            <label>Start Date</label>
            <DatePicker
              startDate={startDate}
              selected={startDate || (isBday ? subtractYears(new Date(), 18) : new Date())}
              onChange={handleStartChange}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              minDate={minDate}
              maxDate={isBday ? subtractYears(new Date(), 18) : new Date()}
            >
            </DatePicker>
          </div>
          <div className='dp-end'>
            <label>End Date</label>
            <DatePicker
              endDate={endDate}
              selected={endDate || (isBday ? subtractYears(new Date(), 18) : new Date())}
              onChange={handleEndChange}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              minDate={minEndDate || minDate}
              maxDate={isBday ? subtractYears(new Date(), 18) : new Date('January 1, 3000')}
            >
            </DatePicker>
          </div>
        </div>
        {isBday && (
          <>
            <br />
            <p className="small">
              To ensure the targets are adults, the latest date that can be entered is 18 years prior to today&rsquo;s date.
            </p>
          </>
        )}
        <hr />
        {selectedRanges
          && <div className="year-editor-section">
            {selectedRanges.map((range) => {
              return (<div className="year-range" key={range}>
                <span className="date-range">{formatDisplayDateRange(range)}</span>
                <span className="delete-icon">
                  <i className="bi bi-trash" onClick={() => handleDeleteRange(range)}/>
                </span>
              </div>);
            })}
          </div>
        }
        
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={toggle}>Close</Button>
        <Button color="primary" className="promote" size="sm" 
          onClick={handleAddRange}
          disabled={!ready}
        >
            Add Selection
        </Button>{" "}
      </ModalFooter>
    </Modal>
  </>);
}
